import { cn } from '@sohanemon/utils';
import { m } from '@sohanscript/i18n';
import { Hitbox } from '@sohanscript/ui/hitbox';
import { Tooltip, TooltipTrigger, TooltipContent } from '@sohanscript/ui/tooltip';
import type { CSSProperties } from 'react';

type MacosActionProps = {
	className?: string;
	onClose?: () => void;
	onMinimize?: () => void;
	onMaximize?: () => void;
	/** Marks the maximize light as active (the window is in fullscreen). */
	isMaximized?: boolean;
};

const BUTTONS = [
	{
		label: m.maca_close,
		hoverColor: '#eb5f59',
		key: 'onClose' as const,
	},
	{
		label: m.maca_minimize,
		hoverColor: '#f8b732',
		key: 'onMinimize' as const,
	},
	{
		label: m.maca_maximize,
		hoverColor: '#3ec84f',
		key: 'onMaximize' as const,
	},
] as const;

export function MacosAction({
	className,
	onClose,
	onMinimize,
	onMaximize,
	isMaximized = false,
}: MacosActionProps) {
	const handlers = { onClose, onMinimize, onMaximize };

	return (
		<div className={cn('mb-4 flex gap-1.5', className)}>
			{BUTTONS.map(({ label, key, hoverColor }) => {
				const handler = handlers[key];
				const isActive = key === 'onMaximize' && isMaximized;
				return (
					<Tooltip key={key}>
						<TooltipTrigger asChild>
							<Hitbox size="xs" aria-hidden={!handler}>
								<button
									type="button"
									aria-hidden={!handler}
									aria-pressed={key === 'onMaximize' ? isMaximized : undefined}
									tabIndex={handler ? undefined : -1}
									aria-label={label()}
									onClick={(e) => {
										if (!handler) return;
										e.stopPropagation();
										handler();
									}}
									style={{ '--hover-color': hoverColor } as CSSProperties}
									className={cn(
										'flex size-3 cursor-default items-center justify-center rounded-full bg-primary/50 transition-colors hover:bg-(--hover-color)',
										isActive && 'bg-(--hover-color)',
									)}
								/>
							</Hitbox>
						</TooltipTrigger>
						<TooltipContent side="top">{label()}</TooltipContent>
					</Tooltip>
				);
			})}
		</div>
	);
}
